import * as React from 'react'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'

interface Props {
  onChoose: (range: [Date, Date]) => void
  initialDates?: [Date, Date]
  disallowChooseFutureDates?: boolean
  disallowDatesBefore?: string // DateStr e.g. '2018-01-01'
  onOpenChange?: (isOpen: boolean) => void
  children?: React.ReactNode
}

interface State {
  date1?: Date
  date2?: Date
  hoverDate?: Date
  shownPage?: {
    month: number
    year: number
  }
}

export class DateRangePicker extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    const {initialDates} = this.props
    const now = new Date()
    let year = now.getFullYear()
    let month = now.getMonth()
    if (initialDates && !!initialDates[0]) {
      year = initialDates[0].getFullYear()
      month = initialDates[0].getMonth()
    }
    this.state = {
      shownPage: {month, year},
      date1: initialDates ? initialDates[0] : undefined,
      date2: initialDates ? initialDates[1] : undefined
    }
  }

  public override componentDidUpdate(prevProps: Props): void {
    if (!this.props.initialDates?.length && !!prevProps.initialDates?.length) {
      this.setState({
        date1: undefined,
        date2: undefined
      })
    }
  }

  private mouseLeave(d: Date): void {
    const {hoverDate} = this.state
    if (hoverDate && d && hoverDate.getTime() === d.getTime()) {
      this.setState({hoverDate: undefined})
    }
  }

  private prevClickDate: number

  private mouseEnter(hoverDate: Date): void {
    this.setState({hoverDate})
  }

  private back(): void {
    let month = this.state.shownPage.month - 1
    let year = this.state.shownPage.year
    if (month < 0) {
      month = 11
      year--
    }
    this.setState({shownPage: {year, month}})
  }

  private forward(): void {
    let month = this.state.shownPage.month + 1
    let year = this.state.shownPage.year
    if (month > 11) {
      month = 0
      year++
    }
    this.setState({shownPage: {year, month}})
  }

  private onClick(date: Date, type: 'click' | 'down' | 'up'): void {
    const sameD1 = this.prevClickDate === date.getTime()
    const bothCh = !!this.state.date1 === !!this.state.date2
    if (
      (type === 'down' && !bothCh && !sameD1) ||
      (type === 'up' && (bothCh || sameD1)) ||
      type === 'click'
    ) {
      return
    }
    this.prevClickDate = date.getTime()
    if (!this.state.date1) {
      this.setState({date1: date})
    } else if (!this.state.date2) {
      this.setState({date2: date})
      const d1 = Time.clone(this.state.date1)
      d1.setHours(12)
      const d2 = Time.clone(date)
      d2.setHours(12)
      if (d1.getTime() < d2.getTime()) {
        this.props.onChoose([d1, d2])
      } else {
        this.props.onChoose([d2, d1])
      }
    } else {
      this.setState({date1: date, date2: undefined})
    }
  }

  // eslint-disable-next-line max-lines-per-function
  public override render(): React.ReactNode {
    const {disallowChooseFutureDates, disallowDatesBefore, children} =
      this.props
    const dd = (d: Date) =>
      d ? Time.formatDate(Time.toTimestamp(d)) : undefined
    const getDay = (date: Date) => (date.getDay() + 6) % 7
    const between = (date: Date, d1: Date, d2: Date) =>
      date &&
      d1 &&
      d2 &&
      ((d1.getTime() < date.getTime() &&
        d2.getTime() > date.getTime() &&
        dd(d1) !== dd(date) &&
        dd(d2) !== dd(date)) ||
        (d2.getTime() < date.getTime() &&
          d1.getTime() > date.getTime() &&
          dd(d1) !== dd(date) &&
          dd(d2) !== dd(date)))
    const left = (date: Date, d1: Date, d2: Date) =>
      date &&
      d1 &&
      d2 &&
      ((d1.getTime() < d2.getTime() && dd(date) === dd(d1)) ||
        (d2.getTime() < d1.getTime() && dd(date) === dd(d2)))
    const right = (date: Date, d1: Date, d2: Date) =>
      date &&
      d1 &&
      d2 &&
      ((d1.getTime() > d2.getTime() && dd(date) === dd(d1)) ||
        (d2.getTime() > d1.getTime() && dd(date) === dd(d2)))

    const page = this.state.shownPage
    const firstCurr = new Date(page.year, page.month, 1)
    const dateArr: Date[][] = [] // week num / weekday
    let weekNum = -1

    for (let i = 1 - getDay(firstCurr); i < 100; i++) {
      const curr = new Date(page.year, page.month, i)
      const day = getDay(curr)
      if (day === 0) {
        weekNum++
        if (curr.getMonth() !== page.month && weekNum > 0) {
          break
        } else {
          dateArr[weekNum] = []
        }
      }
      dateArr[weekNum][day] = curr
    }

    const isDisabled = (d: Date) =>
      (disallowChooseFutureDates &&
        Time.toDateStr(Time.toTimestamp(d)) > Time.toDateStr(Time.now())) ||
      (disallowDatesBefore &&
        Time.toDateStr(Time.toTimestamp(d)) < disallowDatesBefore)

    const cellClassName = (d: Date) => {
      return Util.className({
        'drp-cell': true,
        other: d.getMonth() !== page.month,
        today: Time.areDatesEqual(d, new Date()),
        between:
          (this.state.date1 &&
            this.state.date2 &&
            dd(this.state.date1) !== dd(this.state.date2) &&
            between(d, this.state.date1, this.state.date2)) ||
          (this.state.date1 &&
            !this.state.date2 &&
            this.state.hoverDate &&
            dd(this.state.date1) !== dd(this.state.hoverDate) &&
            between(d, this.state.hoverDate, this.state.date1)),

        chosen:
          (this.state.date1 && dd(this.state.date1) === dd(d)) ||
          (this.state.date2 && dd(this.state.date2) === dd(d)),

        left:
          (this.state.date1 &&
            this.state.date2 &&
            dd(this.state.date1) !== dd(this.state.date2) &&
            left(d, this.state.date1, this.state.date2)) ||
          (this.state.date1 &&
            !this.state.date2 &&
            this.state.hoverDate &&
            dd(this.state.date1) !== dd(this.state.hoverDate) &&
            left(d, this.state.hoverDate, this.state.date1)),

        right:
          (this.state.date1 &&
            this.state.date2 &&
            dd(this.state.date1) !== dd(this.state.date2) &&
            right(d, this.state.date1, this.state.date2)) ||
          (this.state.date1 &&
            !this.state.date2 &&
            this.state.hoverDate &&
            dd(this.state.date1) !== dd(this.state.hoverDate) &&
            right(d, this.state.hoverDate, this.state.date1)),

        disabled: isDisabled(d)
      })
    }

    const dayRow = (day: Date) => {
      const allow = !isDisabled(day)
      const click = allow ? () => this.onClick(day, 'click') : Util.noop
      const up = allow ? () => this.onClick(day, 'up') : Util.noop
      const down = allow ? () => this.onClick(day, 'down') : Util.noop
      const enter = allow ? () => this.mouseEnter(day) : Util.noop
      const leave = allow ? () => this.mouseLeave(day) : Util.noop
      return (
        <td
          data-cnt={day.getDate()}
          className={cellClassName(day)}
          key={day.getTime()}
          onClick={click}
          onMouseUp={up}
          onMouseDown={down}
          onMouseEnter={enter}
          onMouseLeave={leave}
        >
          {day.getDate()}
        </td>
      )
    }

    const days = dateArr.map((week: Date[], ix: number) => (
      <tr key={ix}>{week.map(dayRow)}</tr>
    ))

    const weekDays = Lang.msg.weekNamesShort.map(w => (
      <th className="drp-h" key={w}>
        {w}
      </th>
    ))
    const back = () => this.back()
    const forward = () => this.forward()

    return (
      <div className="drp">
        <div className="drp-head">
          <div className="drp-back" onClick={back}>
            <i className="fa fa-angle-left" />
          </div>
          {Lang.msg.monthNames[this.state.shownPage.month]}{' '}
          {this.state.shownPage.year}
          <div className="drp-forward" onClick={forward}>
            <i className="fa fa-angle-right" />
          </div>
        </div>
        <div className="drp-month">
          <table
            className={
              'drp-table' +
              (this.state.date1 && !this.state.date2 ? ' drp-one' : '')
            }
          >
            <thead className="drp-thead">
              <tr>{weekDays}</tr>
            </thead>
            <tbody className="drp-tbody">{days}</tbody>
          </table>
        </div>
        {children}
      </div>
    )
  }
}
